<template>
  <Modal
    :value="visible"
    :mask-closable="false"
    :footer-hide="true"
    width="800"
    class-name="coupon-edit-modal"
    @on-visible-change="onVisibleChange"
  >
    <div class="modal-header">
      <span class="modal-title">{{ edit_id ? '编辑优惠券' : '新建优惠券' }}</span>
    </div>

    <div class="modal-body">
      <!-- 基础信息 -->
      <div class="section-title">基础信息</div>
      <div class="form-box">
        <div class="form-row">
          <span class="form-label required">优惠券名称</span>
          <div class="form-control">
            <Input v-model="form.name" :maxlength="25" placeholder="请输入优惠券名称" />
            <span class="count-desc">{{ (form.name || '').length }}/25</span>
          </div>
        </div>
        <div class="form-row">
          <span class="form-label required">优惠券类型</span>
          <div class="form-control type-radio-group">
            <RadioGroup v-model="form.coupon_type" :disabled="isEdit">
              <Radio :label="TYPE.FULL_REDUCE" :disabled="isEdit">满减券</Radio>
              <Radio :label="TYPE.DISCOUNT" :disabled="isEdit">折扣券</Radio>
            </RadioGroup>
          </div>
        </div>
        <!-- 满减券：满 X 减 Y -->
        <div v-if="form.coupon_type === TYPE.FULL_REDUCE" class="form-row">
          <span class="form-label required">满减金额</span>
          <div class="form-control amount-row">
            <span>满</span>
            <Input v-model="form.threshold" type="number" placeholder="请输入金额" class="amount-input" :disabled="isEdit" />
            <span>减</span>
            <Input v-model="form.discount" type="number" placeholder="请输入金额" class="amount-input" :disabled="isEdit" />
            <span>元</span>
          </div>
        </div>
        <!-- 折扣券：折扣 -->
        <div v-if="form.coupon_type === TYPE.DISCOUNT" class="form-row">
          <span class="form-label required">折扣</span>
          <div class="form-control amount-row">
            <Input v-model="form.discount" type="number" placeholder="请输入折扣，如8.5表示8.5折" class="discount-input" :disabled="isEdit" />
            <span>折</span>
          </div>
        </div>
        <!-- 折扣券：最高优惠金额封顶 -->
        <div v-if="form.coupon_type === TYPE.DISCOUNT" class="form-row">
          <span class="form-label">最高优惠</span>
          <div class="form-control amount-row">
            <Input v-model="form.max_discount_amount" type="number" placeholder="0=不封顶" class="discount-input" :disabled="isEdit" />
            <span>元</span>
            <span class="suffix-text">（封顶，0 或不填表示不封顶）</span>
          </div>
        </div>
      </div>

      <!-- 领取/使用规则 -->
      <div class="section-title rule-title">领取/使用规则</div>
      <div class="form-box">
        <div class="form-row">
          <span class="form-label required">领取时间 <Tooltip placement="top" max-width="200" content="用户可领取优惠券的时间段"><Icon type="ios-information-circle-outline" /></Tooltip></span>
          <div class="form-control">
            <DatePicker
              v-model="form.receive_time"
              type="datetimerange"
              format="yyyy/MM/dd HH:mm:ss"
              placeholder="开始时间 - 结束时间"
              style="width: 100%"
              :disabled="isEdit"
            ></DatePicker>
          </div>
        </div>
        <div class="form-row">
          <span class="form-label required">使用时间 <Tooltip placement="top" max-width="200" content="优惠券可使用的时间"><Icon type="ios-information-circle-outline" /></Tooltip></span>
          <div class="form-control">
            <RadioGroup v-model="form.use_time_type" :disabled="isEdit">
              <Radio :label="USE_TIME_TYPE.DAYS" :disabled="isEdit">限制有效期天数</Radio>
              <Radio :label="USE_TIME_TYPE.CUSTOM" :disabled="isEdit">自定义时间</Radio>
            </RadioGroup>
          </div>
        </div>
        <div v-if="form.use_time_type === USE_TIME_TYPE.DAYS" class="form-row">
          <span class="form-label"></span>
          <div class="form-control amount-row">
            <span>领券</span>
            <Input v-model="form.use_days" type="number" placeholder="请输入天数" class="days-input" :disabled="isEdit" />
            <span>天</span>
            <span class="suffix-text">内可以使用</span>
          </div>
        </div>
        <div v-if="form.use_time_type === USE_TIME_TYPE.CUSTOM" class="form-row">
          <span class="form-label"></span>
          <div class="form-control">
            <DatePicker
              v-model="form.use_time"
              type="datetimerange"
              format="yyyy/MM/dd HH:mm:ss"
              style="width: 100%"
              :disabled="isEdit"
            ></DatePicker>
          </div>
        </div>
        <div class="form-row">
          <span class="form-label required">发放数量</span>
          <div class="form-control">
            <RadioGroup v-model="form.release_num_type" :disabled="isEdit">
              <Radio :label="LIMIT_TYPE.UNLIMITED" :disabled="isEdit">不限</Radio>
              <Radio :label="LIMIT_TYPE.LIMITED" :disabled="isEdit">限制数量</Radio>
            </RadioGroup>
          </div>
        </div>
        <div v-if="form.release_num_type === LIMIT_TYPE.LIMITED" class="form-row">
          <span class="form-label"></span>
          <div class="form-control amount-row">
            <span>领取数量</span>
            <Input v-model="form.release_num" type="number" placeholder="如10000" class="days-input" :disabled="isEdit" />
            <span>张</span>
          </div>
        </div>
        <div class="form-row">
          <span class="form-label required">每人限领</span>
          <div class="form-control">
            <Select v-model="form.take_limit_num" style="width: 100%">
              <Option v-for="num in perLimitOptions" :key="num" :value="num">{{ num === 0 ? '不限' : num + '张' }}</Option>
            </Select>
          </div>
        </div>
        <div class="form-row">
          <span class="form-label required">适用范围</span>
          <div class="form-control">
            <Select v-model="form.product_condition" style="width: 100%" :disabled="isEdit">
              <Option v-for="opt in scopeOptions" :key="opt.value" :value="opt.value" :disabled="isEdit">{{ opt.label }}</Option>
            </Select>
          </div>
        </div>
        <!-- 指定商品：添加/关联商品 -->
        <div v-if="form.product_condition !== SCOPE.ALL" class="form-row">
          <span class="form-label">{{ form.product_list.length ? '关联商品' : '选择商品' }}</span>
          <div class="form-control">
            <div class="add-product-btn" :class="{ disabled: isEdit }" @click="!isEdit && (productModalVisible = true)">添加商品</div>
            <div v-if="form.product_list.length" class="product-list-box">
              <div v-for="(product, index) in form.product_list" :key="product.uuid" class="product-item">
                <img class="product-image" :src="product.image | default_img" alt="" />
                <div class="product-info">
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-price">¥{{ product.sale_price }}</div>
                </div>
                <img class="icon-remove" :class="{ disabled: isEdit }" src="@/assets/images/icon-result-remove.png" alt="" @click="!isEdit && removeProduct(index)" />
              </div>
            </div>
          </div>
        </div>
        <div class="form-row">
          <span class="form-label">规则说明<br /><span class="label-sub">(选填)</span></span>
          <div class="form-control">
            <Input
              v-model="form.brief"
              type="textarea"
              :rows="3"
              :maxlength="100"
              placeholder="请输入优惠券使用规则说明，如：不与其他优惠叠加，特价商品除外等"
            />
            <span class="count-desc">{{ (form.brief || '').length }}/100</span>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <div class="btn cancel" @click="onCancel">取消</div>
      <div class="btn confirm" :class="{ disabled: submitting }" @click="onConfirm">确定</div>
    </div>

    <!-- 选择管理商品弹窗 -->
    <SelectProductModal :visible.sync="productModalVisible" @confirm="onProductConfirm" />
  </Modal>
</template>

<script>
import SelectProductModal from "./selectProductModal.vue";
import { GetShopCouponDetail, CreateShopCoupon, UpdateShopCoupon } from "@/api/index";

// 优惠券类型（后端 coupon_type: 1=满减, 2=折扣；满0的满减即视为立减券，故前端不再单独提供立减券）
const TYPE = {
  FULL_REDUCE: 1, // 满减券
  DISCOUNT: 3, // 折扣券
};
// 使用时间类型
const USE_TIME_TYPE = {
  DAYS: 1, // 限制有效期天数
  CUSTOM: 2, // 自定义时间
};
// 发放数量类型（与 DB release_num_type 一致：0 不限 / 1 限制数量）
const LIMIT_TYPE = {
  UNLIMITED: 0,
  LIMITED: 1,
};
// 适用范围（与 DB product_condition 一致：0 通用 / 1 部分可用 / 2 部分不可用）
const SCOPE = {
  ALL: 0,
  INCLUDE: 1,
  EXCLUDE: 2,
};

const defaultForm = () => ({
  name: "",
  coupon_type: TYPE.FULL_REDUCE,
  threshold: "", // 满减门槛（DB threshold）
  discount: "", // 满减/立减扣减金额 或 折扣折数（DB discount）
  max_discount_amount: "", // 折扣封顶（DB max_discount_amount，0=不封顶）
  receive_time: [], // 领取时间窗（DB act_start_time / act_end_time）
  use_time_type: USE_TIME_TYPE.DAYS,
  use_days: "", // 领券后 N 天（DB expire_days）
  use_time: [], // 自定义使用时间（DB start_time / end_time）
  release_num_type: LIMIT_TYPE.UNLIMITED, // 发放数量类型（DB release_num_type：0 不限 / 1 限制数量）
  release_num: "", // 发放张数（DB release_num）
  take_limit_num: 0, // 每人限领（DB take_limit_num，0=不限）
  product_condition: SCOPE.ALL, // 商品条件（DB product_condition）
  product_list: [], // 关联商品（提交为 product[]）
  brief: "", // 使用说明（DB brief）
});

// 新建/编辑优惠券弹窗（纯静态表单交互，接口联调见 TODO 标记）
export default {
  name: "CouponEditModal",
  components: {
    SelectProductModal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 编辑时传入优惠券活动 id，0 为新建
    edit_id: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      TYPE,
      USE_TIME_TYPE,
      LIMIT_TYPE,
      SCOPE,
      form: defaultForm(),
      perLimitOptions: [0, 1, 2, 3, 5],
      scopeOptions: [
        { value: SCOPE.ALL, label: "全店商品 (全店通用券)" },
        { value: SCOPE.INCLUDE, label: "指定部分可用商品 (商品券)" },
        { value: SCOPE.EXCLUDE, label: "指定部分不可用商品 (商品券)" },
      ],
      productModalVisible: false,
      submitting: false,
    };
  },
  computed: {
    // 编辑态：仅允许修改 优惠券名称 / 每人限领 / 规则说明，其余字段禁用
    isEdit() {
      return this.edit_id > 0;
    },
  },
  methods: {
    onVisibleChange(val) {
      // 同步父组件 visible：关闭（X/ESC/确认/取消）都要把状态置回，否则 editModalVisible 卡在 true，下次打开不触发
      this.$emit("update:visible", val);
      if (val) {
        this.form = defaultForm();
        this.submitting = false;
        this.productModalVisible = false;
        if (this.edit_id) {
          this.getDetail();
        }
      }
    },
    // 编辑时获取详情并回填（后端字段 → 前端表单）
    getDetail() {
      GetShopCouponDetail({ id: this.edit_id })
        .then((res) => {
          const d = res.data || {};
          // 后端时间存 Y-m-d H:i:s，DatePicker 格式为 yyyy/MM/dd，需转斜杠
          const toPicker = (v) => (v ? String(v).replace(/-/g, "/") : "");
          this.form = {
            name: d.name || "",
            // 后端 coupon_type: 1=满减, 2=折扣（前端无独立立减，立减按满减门槛0处理）
            coupon_type: d.coupon_type === 2 ? TYPE.DISCOUNT : TYPE.FULL_REDUCE,
            threshold: d.threshold || "",
            discount: d.discount || "",
            receive_time:
              d.act_start_time ? [toPicker(d.act_start_time), toPicker(d.act_end_time)] : [],
            use_time_type: d.time_type === 0 ? USE_TIME_TYPE.CUSTOM : USE_TIME_TYPE.DAYS,
            use_days: d.expire_days || "",
            use_time:
              d.time_type === 0 && d.start_time ? [toPicker(d.start_time), toPicker(d.end_time)] : [],
            max_discount_amount: d.max_discount_amount || "",
            release_num_type: d.release_num_type != null ? d.release_num_type : 0,
            release_num: d.release_num ? d.release_num : "",
            take_limit_num: d.take_limit_num || 0,
            product_condition: d.product_condition != null ? d.product_condition : 0,
            product_list: (d.product || []).map((p) => ({
              id: p.product_id,
              name: p.name,
              image: p.image,
              sale_price: p.price,
            })),
            brief: d.brief || "",
          };
        })
        .catch((err) => {
          this.$Message.error((err && err.msg) || "优惠券详情加载失败");
        });
    },
    // 选择商品弹窗确认
    // TODO 接口联调：如需按 id 去重合并而非整体覆盖，在此调整
    onProductConfirm(products) {
      products.forEach((product) => {
        if (!this.form.product_list.some((item) => item.id === product.id)) {
          this.form.product_list.push(product);
        }
      });
    },
    removeProduct(index) {
      this.form.product_list.splice(index, 1);
    },
    onCancel() {
      this.$emit("update:visible", false);
    },
    onConfirm() {
      if (this.submitting) return;
      if (!this.validate()) return;
      const payload = this.buildPayload();
      const api = this.edit_id ? UpdateShopCoupon : CreateShopCoupon;
      this.submitting = true;
      api(payload)
        .then(() => {
          this.submitting = false;
          this.$Message.success(this.edit_id ? "保存成功" : "创建成功");
          this.$emit("confirm", payload);
          this.$emit("update:visible", false);
        })
        .catch((err) => {
          this.submitting = false;
          this.$Message.error((err && err.msg) || (this.edit_id ? "保存失败，请重试" : "创建失败，请重试"));
        });
    },
    // 表单字段 → 后端接口字段映射
    buildPayload() {
      const f = this.form;
      const payload = { name: f.name };
      // 券类型：满减(1)；折扣(3)对应后端 coupon_type=2
      let couponType, threshold = 0, discountVal = 0;
      if (f.coupon_type === TYPE.FULL_REDUCE) {
        couponType = 1;
        threshold = f.threshold;
        discountVal = f.discount;
      } else {
        couponType = 2;
        discountVal = f.discount;
      }
      payload.coupon_type = couponType;
      payload.threshold = threshold;
      payload.discount = discountVal;
      payload.max_discount_amount = f.max_discount_amount ? Number(f.max_discount_amount) : 0;
      // 领取时间窗 → act_start_time / act_end_time（始终传）
      payload.act_start_time =
        f.receive_time && f.receive_time[0] ? this.fmtTime(f.receive_time[0]) : "";
      payload.act_end_time =
        f.receive_time && f.receive_time[1] ? this.fmtTime(f.receive_time[1]) : "";
      // 使用时间：后端 time_type(0 指定时间段 / 1 领券当日 / 2 领券次日)
      if (f.use_time_type === USE_TIME_TYPE.CUSTOM) {
        payload.time_type = 0;
        payload.start_time = f.use_time && f.use_time[0] ? this.fmtTime(f.use_time[0]) : "";
        payload.end_time = f.use_time && f.use_time[1] ? this.fmtTime(f.use_time[1]) : "";
      } else {
        // TODO 前端仅"领券后N天"，对应后端 time_type=1(领券当日)；time_type=2(领券次日)前端未提供入口
        payload.time_type = 1;
        payload.expire_days = f.use_days;
        payload.take_before_time = payload.act_end_time;
      }
      payload.release_num_type = f.release_num_type ? 1 : 0;
      payload.release_num = f.release_num_type ? f.release_num : 0;
      payload.take_limit_num = f.take_limit_num;
      payload.brief = f.brief;
      payload.product_condition = f.product_condition;
      if (f.product_condition) {
        payload.product = f.product_list.map((p) => ({ product_id: p.id }));
      }
      if (this.edit_id) {
        // TODO 后端 update 仅接受 id / name / take_limit_num / brief，其余字段忽略
        return {
          id: this.edit_id,
          name: payload.name,
          take_limit_num: payload.take_limit_num,
          brief: payload.brief,
        };
      }
      return payload;
    },
    fmtTime(date) {
      if (!date) return "";
      const d = new Date(date);
      const p = (n) => String(n).padStart(2, "0");
      return (
        d.getFullYear() +
        "-" +
        p(d.getMonth() + 1) +
        "-" +
        p(d.getDate()) +
        " " +
        p(d.getHours()) +
        ":" +
        p(d.getMinutes()) +
        ":" +
        p(d.getSeconds())
      );
    },
    // 表单校验（纯前端，联调时可按接口要求调整规则）
    validate() {
      if (!this.form.name) {
        this.$Message.warning("请输入优惠券名称");
        return false;
      }
      if (this.form.coupon_type === TYPE.FULL_REDUCE && (!this.form.threshold || !this.form.discount)) {
        this.$Message.warning("请输入满减金额");
        return false;
      }
      if (this.form.coupon_type === TYPE.DISCOUNT && !this.form.discount) {
        this.$Message.warning("请输入折扣");
        return false;
      }
      if (!this.form.receive_time || this.form.receive_time.length !== 2 || !this.form.receive_time[0]) {
        this.$Message.warning("请选择领取时间");
        return false;
      }
      if (this.form.use_time_type === USE_TIME_TYPE.DAYS && !this.form.use_days) {
        this.$Message.warning("请输入有效期天数");
        return false;
      }
      if (this.form.use_time_type === USE_TIME_TYPE.CUSTOM && (!this.form.use_time || this.form.use_time.length !== 2 || !this.form.use_time[0])) {
        this.$Message.warning("请选择使用时间");
        return false;
      }
      if (this.form.release_num_type && !this.form.release_num) {
        this.$Message.warning("请输入领取数量");
        return false;
      }
      if (this.form.release_num && Number(this.form.release_num) <= 0) {
        this.$Message.warning("领取数量需大于 0");
        return false;
      }
      if (this.form.product_condition && !this.form.product_list.length) {
        this.$Message.warning("请添加关联商品");
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .coupon-edit-modal {
  text-align: left;
  line-height: 1.5;
  .ivu-modal-content {
    border-radius: 10px;
  }

  .ivu-modal-body {
    padding: 0;
  }

  .ivu-modal-close {
    top: 16px;
    right: 16px;
  }
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 24px 8px 24px;
  border-bottom: 1px solid #f0f0f0;
  position: relative;

  .modal-title {
    font-size: 18px;
    font-weight: bold;
    color: #333333;
  }
}

.modal-body {
  padding: 0px 32px 24px 32px;
  max-height: 60vh;
  overflow: auto;
  text-align: left;

  .section-title {
    margin-top: 18px;
    font-weight: bold;
    font-size: 16px;
    color: #333333;

    &.rule-title {
      margin-top: 20px;
    }
  }
  .form-box {
    margin-top: 10px;
    padding: 16px;
    background: #F8F9FB;
    border-radius: 8px 8px 8px 8px;
    .form-row:first-child {
      margin-top: 0;
    }
  }
  
  
  .form-row {
    margin-top: 16px;
    display: flex;
    align-items: flex-start;

    .form-label {
      flex-shrink: 0;
      width: 110px;
      height: 30px;
      font-size: 14px;
      color: #666666;
      text-align: right;
      padding-right: 12px;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      .required-label,
      &.required::before {
        content: "*";
        color: #ff5967;
        margin-right: 2px;
      }

      .label-sub {
        font-size: 12px;
        color: #999999;
      }
    }

    .form-control {
      flex: 1;
      min-width: 0;
      position: relative;

      .count-desc {
        position: absolute;
        right: 10px;
        bottom: 6px;
        font-size: 12px;
        color: #999999;
        pointer-events: none;
      }
    }

    .type-radio-group {
      padding-top: 2px;
    }

    .amount-row {
      display: flex;
      align-items: center;
      gap: 8px;

      span {
        flex-shrink: 0;
        font-size: 14px;
        color: #333333;
      }

      .amount-input {
        width: 160px;
      }

      .amount-input.with-unit {
        width: 240px;
      }

      .discount-input {
        width: 240px;
      }

      .days-input {
        width: 140px;
      }

      .suffix-text {
        color: #666666;
      }
    }

    .add-product-btn {
      display: inline-block;
      padding: 6px 16px;
      background: #ffffff;
      border: 1px solid #ff5967;
      border-radius: 4px;
      font-size: 14px;
      color: #ff5967;
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
        border-color: #dddddd;
        color: #999999;
      }
    }

    .product-list-box {
      margin-top: 8px;

      .product-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px;
        background-color: #ffffff;
        border-radius: 8px;

        & + .product-item {
          margin-top: 8px;
        }

        .product-image {
          width: 40px;
          height: 40px;
          flex-shrink: 0;
          border-radius: 8px;
          object-fit: cover;
        }

        .product-info {
          flex: 1;
          min-width: 0;

          .product-name {
            font-size: 12px;
            color: #333333;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .product-price {
            margin-top: 4px;
            font-size: 12px;
            color: #ff5967;
          }
        }

        .icon-remove {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          cursor: pointer;

          &.disabled {
            opacity: 0.4;
            cursor: not-allowed;
            pointer-events: none;
          }
        }
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 20px 24px 24px;

  .btn {
    width: 96px;
    height: 40px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    cursor: pointer;

    &.cancel {
      background: #ffffff;
      border: 1px solid #e3e3e3;
      color: #999999;

      &:hover {
        border-color: #cccccc;
      }
    }

    &.confirm {
      background: #ff5967;
      color: #ffffff;

      &:hover {
        opacity: 0.9;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

/* 覆盖选中时的背景色和边框色 */
/* 1. 覆盖选中状态的外层边框和背景 */
::v-deep   .ivu-radio-checked .ivu-radio-inner {
  border-color: #FF5967 !important;
}

/* 2. ⚠️ 核心：覆盖内部圆点的颜色（伪元素 ::after） */
::v-deep   .ivu-radio-checked .ivu-radio-inner::after {
  background-color: #FF5967 !important; /* 将圆点改为你想要的颜色 */
}
::v-deep .ivu-radio-wrapper {
  line-height: 30px;
}
</style>
